import useAutomationValues from './useAutomationValues';
import { isInboxDeleteRequested } from 'dashboard/helper/inbox';

import {
  getCustomAttributeInputType,
  filterCustomAttributes,
  getStandardAttributeInputType,
  isCustomAttribute,
} from 'dashboard/helper/automationHelper';

export function useEditableAutomation() {
  const { getConditionDropdownValues, getActionDropdownValues, inboxes } =
    useAutomationValues();

  /**
   * This function sets the conditions for automation.
   * It help to format the conditions for the automation when we open the edit automation modal.
   * @param {Object} automation - The automation object containing conditions to manifest.
   * @param {Array} allCustomAttributes - List of all custom attributes.
   * @param {Object} automationTypes - Object containing automation type definitions.
   * @returns {Array} An array of manifested conditions.
   */
  const manifestConditions = (
    automation,
    allCustomAttributes,
    automationTypes
  ) => {
    const customAttributes = filterCustomAttributes(allCustomAttributes);
    return automation.conditions.map(condition => {
      const customAttr = isCustomAttribute(
        customAttributes,
        condition.attribute_key
      );
      let inputType = 'plain_text';
      if (customAttr) {
        inputType = getCustomAttributeInputType(customAttr.type);
      } else {
        inputType = getStandardAttributeInputType(
          automationTypes,
          automation.event_name,
          condition.attribute_key
        );
      }
      // A condition may reference an attribute/definition that no longer
      // resolves (e.g. a custom attribute that was deleted or not yet loaded).
      // Fall back to plain text so the edit modal still opens instead of
      // throwing further down.
      if (!inputType) {
        inputType = 'plain_text';
      }
      const values = condition.values ?? [];
      if (inputType === 'plain_text' || inputType === 'date') {
        return { ...condition, values: values[0] };
      }
      if (inputType === 'comma_separated_plain_text') {
        return { ...condition, values: values.join(',') };
      }
      return {
        ...condition,
        query_operator: condition.query_operator || 'and',
        values: [
          ...(getConditionDropdownValues(condition.attribute_key) || []),
        ].filter(item => [...values].includes(item.id)),
      };
    });
  };

  /**
   * Generates an array of actions for the automation.
   * @param {Object} action - The action object.
   * @param {Array} automationActionTypes - List of available automation action types.
   * @returns {Array|Object} Generated actions array or object based on input type.
   */
  const generateActionsArray = (action, automationActionTypes) => {
    const params = action.action_params ?? [];
    // The action may reference an action type that is no longer part of the
    // catalog (e.g. a custom action removed from a build). Guard the lookup so
    // an unknown action degrades to a raw params passthrough instead of
    // throwing and breaking the whole edit modal.
    const actionType = automationActionTypes.find(
      item => item.key === action.action_name
    );
    const inputType = actionType?.inputType;
    if (inputType === 'multi_select' || inputType === 'search_select') {
      return [...(getActionDropdownValues(action.action_name) || [])].filter(
        item => [...params].includes(item.id)
      );
    }
    if (inputType === 'team_message') {
      return {
        team_ids: [
          ...(getActionDropdownValues(action.action_name) || []),
        ].filter(item => [...(params[0]?.team_ids ?? [])].includes(item.id)),
        message: params[0]?.message,
      };
    }
    if (inputType === 'inbox_message') {
      // an object, not [object]: the payload generator would turn an array of objects into their ids
      const value = { ...(params[0] ?? {}) };
      // an inbox marked for deletion is not offered: drop it, so the empty picker means an
      // empty value and the validation asks for a new one (API rules may store the id as a string)
      const savedInbox = inboxes?.value?.find(
        inbox => inbox.id === Number(value.inbox_id)
      );
      if (isInboxDeleteRequested(savedInbox)) value.inbox_id = null;
      return value;
    }
    if (inputType === 'sms_message') {
      const value = { ...(params[0] ?? {}) };
      // only the inboxes that can send SMS are offered: once the inboxes are loaded, drop any
      // other (deleted, marked for deletion), so the empty picker means an empty value and the
      // validation asks for a new one (API rules may store the id as a string)
      const offered = (getActionDropdownValues(action.action_name) || []).some(
        inbox => inbox.id === Number(value.inbox_id)
      );
      if (inboxes?.value?.length && !offered) value.inbox_id = null;
      return value;
    }
    return [...params];
  };

  /**
   * This function sets the actions for automation.
   * It help to format the actions for the automation when we open the edit automation modal.
   * @param {Object} automation - The automation object containing actions.
   * @param {Array} automationActionTypes - List of available automation action types.
   * @returns {Array} An array of manifested actions.
   */
  const manifestActions = (automation, automationActionTypes) => {
    return automation.actions.map(action => {
      const result = {
        ...action,
        action_params: action.action_params?.length
          ? generateActionsArray(action, automationActionTypes)
          : [],
      };
      if (action.action_blob_ids) {
        result.action_blob_ids = action.action_blob_ids;
      }
      return result;
    });
  };

  /**
   * Formats the automation object for use when we edit the automation.
   * It help to format the conditions and actions for the automation when we open the edit automation modal.
   * @param {Object} automation - The automation object to format.
   * @param {Array} allCustomAttributes - List of all custom attributes.
   * @param {Object} automationTypes - Object containing automation type definitions.
   * @param {Array} automationActionTypes - List of available automation action types.
   * @returns {Object} A new object with formatted automation data, including automation conditions and actions.
   */
  const formatAutomation = (
    automation,
    allCustomAttributes,
    automationTypes,
    automationActionTypes
  ) => {
    return {
      ...automation,
      conditions: manifestConditions(
        automation,
        allCustomAttributes,
        automationTypes
      ),
      actions: manifestActions(automation, automationActionTypes),
    };
  };

  return { formatAutomation };
}
